import { useAuth0 } from '@auth0/auth0-react';
import Axios from 'axios';
import React, { useCallback, useMemo, useState } from 'react';
import { useRef } from 'react';
import { useContext } from 'react';
import { useEffect } from 'react';
import AsyncSelect from 'react-select/async';
import { store } from 'components/Store';
import debounce from 'components/Dashboard/helpers/debounce';
import useSetFiltersToLocalStorageByType from 'components/Dashboard/Filters/useSetFiltersToLocalStorageByType';
import { colourStyles } from './SelectFilterUtils';
import useGetAccessToken from 'components/Dashboard/apiCalls/useGetAccessToken';
import { usStateOptions } from 'components/Dashboard/Filters/FiltersData/LegislationFilters';
import { usAgencyOptions, usDepartmentOptions } from 'components/Dashboard/Filters/FiltersData/RegulationsFilters';
import {
  checkIfOptionSelected,
  emptyMessage,
  optionType,
  placeholderText,
  SelectOption,
} from 'components/Dashboard/Filters/ui/SelectFilter';

const MembershipSelectFilter = ({
  disabled,
  filter,
  selectOption,
  useInPopUp,
  isExpanded,
  singleItem,
  placeholder,
}) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { net_api_url, team } = state;
  const { type, options, subtype } = filter;
  const { getAccessToken } = useGetAccessToken();
  const { isAuthenticated } = useAuth0();
  const { setFiltersLocalStorageByType } = useSetFiltersToLocalStorageByType();
  const [hasSearched, setHasSearched] = useState(false);

  let axiosCancelToken = useRef(null);

  const createRoute = () => {
    switch (subtype) {
      case 'MembershipOfAPPGs':
        return 'appg';
      case 'MembershipOfSelectCommittees':
        return 'select-committee';
      case 'MembershipOfLordsSelectCommittees':
        return 'lords-select-committee';
      case 'ConsultationSource':
        return 'consultation-source';
      case 'CommitteeForPublication':
        return 'committee-for-publications';
      case 'DepartmentSelectType':
        return 'department-for-stakeholders';
      case 'usAgencyOptions':
        return 'us-agency';
      default:
        return null;
    }
  };

  const getTeamMembersOptions = (inputValue) => {
    const membersStringified = team?.allMembers
      .map((member) => {
        return {
          fullName: member.fullName,
          emailAddress: member.emailAddress,
          id: member.id,
        };
      })
      .map((member) => JSON.stringify(member));

    const filteredMembers = membersStringified
      .filter((memberStr) => memberStr.toLowerCase().includes(inputValue.toLowerCase()))
      .map((memberStr) => JSON.parse(memberStr));
    return filteredMembers.map((member) => {
      return {
        label: `${member.fullName} <${member.emailAddress}>`,
        value: `${member.id}`,
        hash: `${member.id}`,
      };
    });
  };

  const getSpecificOptions = (inputValue, options) => {
    const optionsToUse = options ?? usStateOptions;
    const filteredOptions = optionsToUse.filter((stateStr) =>
      stateStr?.name?.toLowerCase().includes(inputValue.toLowerCase())
    );
    return filteredOptions.map((member) => {
      return {
        label: `${member.name}`,
        value: `${member.name}`,
        hash: `${member.name}`,
      };
    });
  };

  useEffect(() => {
    if (isExpanded) {
      setTimeout(() => {
        selectRef.current.focus();
      }, 100);
    }
  }, [isExpanded]);

  const getAsyncOptions = async (inputValue) => {
    setHasSearched(inputValue.trim() !== '');

    if (axiosCancelToken.current) axiosCancelToken.current.abort();
    axiosCancelToken.current = new AbortController();

    try {
      const token = isAuthenticated ? await getAccessToken() : '';
      const requestOptions = {
        signal: axiosCancelToken.current.signal,
        ...(isAuthenticated ? { headers: { Authorization: `Bearer ${token}` } } : {}),
      };

      const urlRoute = createRoute();
      if (urlRoute) {
        const callResults = await Axios.get(
          `${net_api_url}/api/autocomplete/${urlRoute}?term=${inputValue}`,
          requestOptions
        );
        return callResults.data;
      }

      if (subtype === 'CrmContacts') {
        const res = await Axios.get(`${net_api_url}/api/autocomplete/crm-contact?term=${inputValue}`, requestOptions);
        return (res.data ?? []).map((c) => ({
          label: c.fullName,
          value: String(c.crmContactId),
          hash: String(c.crmContactId),
        }));
      }
      if (subtype === 'CrmInteractionTags') {
        const res = await Axios.get(
          `${net_api_url}/api/autocomplete/crm-interaction-tag?term=${inputValue}`,
          requestOptions
        );
        return (res.data ?? []).map((t) => ({
          label: t.label,
          value: t.value,
          hash: t.value,
        }));
      }
      if (subtype === 'Teammates') return getTeamMembersOptions(inputValue);
      if (subtype === 'usStateOptions') return getSpecificOptions(inputValue);
      if (subtype === 'usDepartmentOptions') return getSpecificOptions(inputValue, usDepartmentOptions);
      if (subtype === 'usAgencyOptions') return getSpecificOptions(inputValue, usAgencyOptions);
    } catch (error) {
      return [];
    }
  };

  //AE: Source https://github.com/JedWatson/react-select/issues/614
  const getOptions = useCallback(
    debounce((inputText, callback) => {
      getAsyncOptions(inputText).then((options) => callback(options));
    }, 500),
    []
  );

  const selectRef = useRef(null);

  const selectStyles = useMemo(
    () => ({
      ...colourStyles,
      control: (styles) => ({
        ...colourStyles.control(styles),
        ...(disabled ? { paddingLeft: '5px', cursor: 'auto' } : {}),
      }),
    }),
    [disabled]
  );

  const handleChange = (selected, action) => {
    if (action.option) {
      const lastSelected = {
        name: action.option.label,
        selected: true,
        filterValue:
          subtype === 'Teammates' || subtype === 'CrmContacts' || subtype === 'CrmInteractionTags'
            ? action.option.value
            : action.option.label,
        // getAsyncOptions already returns a stable id-based hash for id-backed subtypes (falls back
        // to the name for name-identity ones like tags/US states) - carrying it through here is what
        // lets filtersHashes.js write a working hash instead of the display name, which would send a
        // name string to a field expecting a numeric id and silently match nothing on reload.
        hash: action.option.hash,
      };

      let valueToState = [...options, lastSelected];

      //remove previous value if exists
      if (checkIfOptionSelected(action.option.label, options)) {
        valueToState = valueToState.filter((option) => !(option.name === action.option.label));
      }
      valueToState = valueToState.filter((option) => !(option.name === action.option.label && !option.selected));
      selectOption({ filter, newValue: valueToState });

      if (!useInPopUp) {
        setFiltersLocalStorageByType({ type, options: valueToState });
      }
    } else if (!!selected && singleItem) {
      selectOption({
        filter,
        newValue: [
          {
            ...selected,
            name: selected?.label,
            filterValue: selected.value ?? selected.label,
          },
        ],
      });
    }
  };

  return (
    <div className='box-rounded mb-4 membership-main-container'>
      <div className='filter-select-container'>
        {!disabled && <span className='main-search-icon' />}
        <AsyncSelect
          ref={selectRef}
          loadOptions={getOptions}
          cacheOptions={true}
          components={{
            Option: (props) => <SelectOption {...props} optionType={optionType(subtype)} options={options} />,
            MultiValueContainer: () => null,
            IndicatorSeparator: () => null,
            DropdownIndicator: () => null,
            ClearIndicator: () => null,
          }}
          isMulti={!singleItem}
          openMenuOnFocus={singleItem}
          noOptionsMessage={() => {
            if (!hasSearched) return null;
            return <>{emptyMessage(subtype)}</>;
          }}
          styles={selectStyles}
          placeholder={placeholder ?? placeholderText(subtype)}
          onChange={handleChange}
          isDisabled={disabled}
          openMenuOnClick={singleItem}
          hideSelectedOptions={false}
          classNamePrefix={'react-custom-select-contacts'}
          onInputChange={(newValue) => {
            if (newValue.trim() === '') setHasSearched(false);
          }}
        />
      </div>
    </div>
  );
};

export default MembershipSelectFilter;
